<template>
  <div class="app-shell">
    <app-header
      :is-authenticated="appState.isAuthenticated"
      :is-admin="appState.isAdmin"
      :login="appState.user.login"
      :user-rank="appState.user.rank"
      :theme="theme"
      @open-login="openModal('login')"
      @open-register="openModal('register')"
      @logout="handleLogout"
      @toggle-theme="toggleTheme"
    />

    <section v-if="appState.banNotice" class="ban-notice" role="alert">
      <div>
        <strong>Аккаунт заблокирован</strong>
        <p>{{ appState.banNotice.message }}</p>
        <p>
          Если вы считаете, что вас несправедливо забанили - пишите на почту
          <a href="mailto:spring_app@mail.ru">spring_app@mail.ru</a>.
        </p>
      </div>
      <button type="button" aria-label="Закрыть уведомление о блокировке" @click="clearBanNotice">
        ×
      </button>
    </section>

    <main class="app-main">
      <router-view @open-login="openModal('login')" @open-register="openModal('register')" />
    </main>

    <app-footer />

    <auth-modal
      :mode="activeModal"
      @close="closeModal"
      @switch-mode="openModal"
      @login-success="handleLoginSuccess"
      @register-success="handleRegisterSuccess"
      @account-banned="handleAccountBanned"
    />

    <preference-modal
      :is-open="shouldShowPreferenceModal"
      :initial-preferences="appState.user.preferences"
      :tag-options="preferenceTagOptions"
      :difficulty-options="difficultyPreferenceOptions"
      :saving="preferenceSaving"
      :error-message="preferenceError"
      @save="handlePreferenceSave"
      @skip="handlePreferenceSkip"
    />
  </div>
</template>

<script>
import AppFooter from './components/AppFooter.vue'
import AppHeader from './components/AppHeader.vue'
import AuthModal from './components/AuthModal.vue'
import PreferenceModal from './components/PreferenceModal.vue'
import {
  checkAdminAccess,
  getCurrentUserProfile,
  getUserPreferences,
  listCases,
  listCaseTags,
  listFavoriteCases,
  listMyAchievements,
  logoutRequest,
  mapApiProfileToState,
  saveUserPreferences,
} from './api/authApi'
import {
  appState,
  clearBanNotice,
  loginUser,
  logoutUser,
  registerUser,
  setAdminAccess,
  skipUserPreferences,
  setUserFavoriteCases,
  setUserAchievements,
  showBanNotice,
  updateUserPreferences,
  getDifficultyPreferenceOptions,
  getPreferenceTagOptions,
  setCases,
  setAvailableTags,
  setCasesError,
  setCasesLoading,
} from './store/appState'

export default {
  name: 'App',
  components: { AppFooter, AppHeader, AuthModal, PreferenceModal },
  data() {
    return {
      activeModal: null,
      appState,
      theme: localStorage.getItem('theme') || 'light',
      difficultyPreferenceOptions: getDifficultyPreferenceOptions(),
      preferenceSaving: false,
      preferenceError: '',
    }
  },
  computed: {
    preferenceTagOptions() {
      return getPreferenceTagOptions()
    },
    shouldShowPreferenceModal() {
      return (
        this.appState.isAuthenticated &&
        this.appState.shouldShowPreferencesOnboarding &&
        this.$route.name === 'dashboard'
      )
    },
  },
  created() {
    document.documentElement.setAttribute('data-theme', this.theme)
    this.loadCases()
    this.validateStoredSession()
  },
  methods: {
    async loadCases() {
      setCasesLoading(true)
      setCasesError('')
      try {
        const [casesResult, tagsResult] = await Promise.allSettled([listCases(), listCaseTags()])
        if (casesResult.status === 'rejected') throw casesResult.reason
        setCases(casesResult.value)
        setAvailableTags(tagsResult.status === 'fulfilled' ? tagsResult.value : [])
      } catch (error) {
        setCases([])
        setCasesError(error?.message || 'Не удалось загрузить кейсы.')
      } finally {
        setCasesLoading(false)
      }
    },
    async validateStoredSession() {
      try {
        const profile = await getCurrentUserProfile()
        loginUser(mapApiProfileToState(profile, this.appState.user))
        await Promise.all([this.loadPersonalization(), this.refreshAdminAccess()])
      } catch {
        if (this.appState.isAuthenticated) {
          logoutUser()
        }
        if (this.$route.meta.requiresAuth) {
          this.$router.replace('/')
        }
      }
    },
    toggleTheme() {
      this.theme = this.theme === 'dark' ? 'light' : 'dark'
      document.documentElement.setAttribute('data-theme', this.theme)
      localStorage.setItem('theme', this.theme)
    },
    openModal(type) {
      this.activeModal = type
    },
    closeModal() {
      this.activeModal = null
    },
    async handleLoginSuccess(payload) {
      clearBanNotice()
      loginUser(payload)
      this.closeModal()
      this.$router.push('/dashboard')
      await Promise.all([this.loadPersonalization(), this.refreshAdminAccess()])
    },
    async handleRegisterSuccess(payload) {
      registerUser(payload)
      setAdminAccess(false)
      this.closeModal()
      this.$router.push('/dashboard')
      await this.loadPersonalization({ preserveOnboarding: true })
    },
    async refreshAdminAccess() {
      try {
        setAdminAccess(await checkAdminAccess())
      } catch {
        setAdminAccess(false)
      }
    },
    handleAccountBanned(message) {
      showBanNotice(message)
      this.closeModal()
    },
    async loadPersonalization({ preserveOnboarding = false } = {}) {
      const [preferencesResult, favoritesResult, achievementsResult] = await Promise.allSettled([
        getUserPreferences(),
        listFavoriteCases(),
        listMyAchievements(),
      ])
      if (preferencesResult.status === 'fulfilled') {
        updateUserPreferences(preferencesResult.value, { closeOnboarding: !preserveOnboarding })
      }
      if (favoritesResult.status === 'fulfilled') {
        setUserFavoriteCases(favoritesResult.value)
      }
      if (achievementsResult.status === 'fulfilled') {
        setUserAchievements(achievementsResult.value)
      }
    },
    async handlePreferenceSave(payload) {
      this.preferenceSaving = true
      this.preferenceError = ''
      try {
        const preferences = await saveUserPreferences(payload)
        const selectedNames = this.preferenceTagOptions
          .filter((tag) => preferences.tagIds.includes(Number(tag.id)))
          .map((tag) => tag.name)
        updateUserPreferences({
          ...preferences,
          tags: preferences.tags.length ? preferences.tags : selectedNames,
        })
      } catch (error) {
        this.preferenceError = error?.message || 'Не удалось сохранить предпочтения.'
      } finally {
        this.preferenceSaving = false
      }
    },
    async handlePreferenceSkip() {
      this.preferenceSaving = true
      this.preferenceError = ''
      try {
        await saveUserPreferences()
        skipUserPreferences()
      } catch (error) {
        this.preferenceError = error?.message || 'Не удалось сохранить предпочтения.'
      } finally {
        this.preferenceSaving = false
      }
    },
    async handleLogout() {
      try {
        await logoutRequest()
      } catch {
        // Сессия уже могла завершиться
      }
      logoutUser()
      this.$router.push('/')
    },
  },
}
</script>

<style scoped>
.app-shell {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.app-main {
  flex: 1;
  padding: clamp(18px, 3vw, 40px) 0;
}

.ban-notice {
  width: min(1120px, calc(100% - 32px));
  margin: 14px auto 0;
  padding: 12px 14px;
  border: 1px solid #f5a3a3;
  border-radius: 0;
  background: #fff1f0;
  color: #7a1212;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.ban-notice strong,
.ban-notice p {
  margin: 0;
}

.ban-notice p {
  margin-top: 4px;
}

.ban-notice a {
  color: inherit;
  overflow-wrap: anywhere;
}

.ban-notice button {
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font-size: 1.35rem;
  line-height: 1;
}
</style>
